import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { logger } from "../../shared/utils/logger";

// Language display names mapping
const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی' },
  { code: 'fr-CA', name: 'French (Canada)', nativeName: 'Français (Canada)' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl ayisyen' },
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'km', name: 'Khmer', nativeName: 'ខ្មែរ' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'yi', name: 'Yiddish', nativeName: 'ייִדיש' },
  { code: 'zh', name: 'Chinese (Traditional)', nativeName: '中文 (繁體)' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)', nativeName: '中文 (简体)' },
];

export function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale: urlLocale } = useParams<{ locale?: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0]!;

  // Debug: Log language state
  useEffect(() => {
    logger.log('[LanguageSelector] URL locale:', urlLocale);
    logger.log('[LanguageSelector] i18n language:', i18n.language);
    logger.log('[LanguageSelector] Current language:', currentLanguage.code);
    logger.log('[LanguageSelector] Translation test:', t('languageSelector.title'));
  }, [urlLocale, i18n.language, currentLanguage, t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageSelect = (langCode: string) => {
    // Change i18n language
    i18n.changeLanguage(langCode);
    setIsOpen(false);

    // Keep the query string: ConsumerProvider reads `?consumer=` live, so dropping
    // it mid-flow strips the partner logo and theme.
    const newPath = urlLocale
      ? location.pathname.replace(`/${urlLocale}`, `/${langCode}`)
      : `/${langCode}${location.pathname}`;
    navigate(`${newPath}${location.search}`, { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent, langCode: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLanguageSelect(langCode);
    }
  };

  return (
    <aside className="language" aria-label="Select language">
      <div className={`language-selection ${isOpen ? 'is-active' : ''}`}>
        <button
          ref={toggleRef}
          type="button"
          className="language-selection-toggle"
          onClick={handleToggle}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls="lang-listbox"
        >
          {currentLanguage.nativeName}
        </button>
        {isOpen && (
          <>
            <div className="language-overlay" onClick={() => setIsOpen(false)} />
            <div
              ref={modalRef}
              id="lang-listbox"
              className="language-selection-list"
              role="listbox"
              aria-label={t('languageSelector.title')}
            >
              <div className="option m_title">{t('languageSelector.title')}</div>
              {LANGUAGES.map((lang) => (
                <div
                  key={lang.code}
                  className="option"
                  role="option"
                  aria-selected={lang.code === i18n.language}
                  tabIndex={0}
                  lang={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  onKeyDown={(e) => handleKeyDown(e, lang.code)}
                >
                  {lang.nativeName}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
